<template>
  <div v-if="dialogVisible" class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="handleClose"></div>

    <!-- Dialog -->
    <div class="relative min-h-screen flex items-center justify-center p-4">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl relative z-50 h-[80vh] flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 border-b dark:border-gray-700 flex-shrink-0">
          <h3 class="text-lg font-semibold dark:text-gray-100">编辑用户: {{ user?.username }}</h3>
        </div>

        <!-- Tabs -->
        <div class="border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <nav class="flex -mb-px px-6" aria-label="Tabs">
            <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id" :class="[
              activeTab === tab.id
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300',
              'whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm',
            ]">
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <div class="px-6 py-4 flex-1 overflow-y-auto">
          <!-- Tab 1: 基本信息 -->
          <UserBasicInfo v-show="activeTab === 'basic'" :user="user" :roleMap="roleMap" v-model="formData.role" />

          <!-- Tab 2: 订阅管理 -->
          <UserSubscriptionManagement v-show="activeTab === 'subscription'" :user="user"
            @subscription-updated="handleDataUpdated" />

          <!-- Tab 3: 配额管理 -->
          <UserQuotaManagement v-show="activeTab === 'quota'" :user="user" @quota-updated="handleDataUpdated" />
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg flex justify-end space-x-3 flex-shrink-0">
          <button type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            @click="handleClose">
            关闭
          </button>
          <button v-if="activeTab === 'basic'" type="button" :disabled="loading"
            class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="handleSaveRole">
            {{ loading ? '保存中...' : '保存角色' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { User } from '@/types/user/user';
import { UserRole } from '@/types/auth/role';
import { searchUsersAPI, updateUserRoleAPI } from '@/api/user';
import toast from '@/utils/toaster';
import UserBasicInfo from './UserBasicInfo.vue';
import UserSubscriptionManagement from './UserSubscriptionManagement.vue';
import UserQuotaManagement from './UserQuotaManagement.vue';

const props = defineProps<{
  visible: boolean;
  user: User | null;
  roleMap: Record<UserRole, string>;
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'user-updated', user?: User): void;
}>();

const dialogVisible = ref(props.visible);
const loading = ref(false);

// Tabs
const tabs = [
  { id: 'basic', label: '基本信息' },
  { id: 'subscription', label: '订阅管理' },
  { id: 'quota', label: '配额管理' },
];
const activeTab = ref('basic');

// Form data
const formData = ref({
  role: UserRole.USER,
});

/**
 * 刷新用户数据
 */
const refreshUserData = async () => {
  if (!props.user) return;

  try {
    // 使用 searchUsersAPI 重新获取用户数据
    // 通过用户名精确查询（因为用户名是唯一的）
    const response = await searchUsersAPI({
      query: props.user.username,
      page: 1,
      limit: 1,
    });

    if (response.data.users && response.data.users.length > 0) {
      const updatedUser = response.data.users[0];
      // 通知父组件更新用户数据
      emit('user-updated', updatedUser);
    }
  } catch (error: any) {
    console.error('刷新用户数据失败:', error);
    // 静默失败，不影响用户体验
  }
};

/**
 * 处理数据更新（订阅/配额更新后）
 */
const handleDataUpdated = async () => {
  // 刷新用户数据
  await refreshUserData();
  // 通知父组件刷新用户列表
  emit('user-updated');
};

/**
 * 关闭对话框
 */
const handleClose = () => {
  dialogVisible.value = false;
  activeTab.value = 'basic';
};

/**
 * 保存角色变更
 */
const handleSaveRole = async () => {
  if (!props.user) return;
  if (formData.value.role === props.user.role) {
    toast.info('角色未变更');
    return;
  }
  loading.value = true;
  try {
    const response = await updateUserRoleAPI(props.user.id, formData.value.role);
    if (response.status === 200) {
      toast.success('角色更新成功');
      await refreshUserData();
      emit('user-updated');
    } else {
      toast.error(response.message || '角色更新失败');
    }
  } catch (error: any) {
    toast.error(error?.message || '角色更新失败');
  } finally {
    loading.value = false;
  }
};

// Watch for changes in props
watch(
  () => props.visible,
  (newVal) => {
    dialogVisible.value = newVal;
  }
);

watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      formData.value = {
        role: newUser.role,
      };
    }
  },
  { immediate: true }
);

watch(dialogVisible, (newVal) => {
  emit('update:visible', newVal);
});
</script>

<style scoped>
/* 添加淡入淡出动画 */
.fixed {
  animation: fadeIn 0.2s ease -in -out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
